import apiService from './api.service';

export enum MissionType {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  ONE_TIME = 'ONE_TIME',
}

export enum MissionAction {
  CHECK_IN = 'CHECK_IN',
  PLAY_GAME = 'PLAY_GAME',
  PLAY_NEW_GAME = 'PLAY_NEW_GAME',
  COMPLETE_PROFILE = 'COMPLETE_PROFILE',
  INVITE_FRIEND = 'INVITE_FRIEND',
}

export interface Mission {
  _id: string;
  name: string;
  description: string;
  imageUrl: string;
  type: MissionType;
  action: MissionAction;
  gameId?: string;
  points: number;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MissionsResponse {
  docs: Mission[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

class MissionService {
  // Admin methods
  getAllMissions(
    page = 1,
    limit = 10,
    type?: MissionType,
  ): Promise<MissionsResponse> {
    return apiService
      .get('/missions/all', { params: { page, limit, type } })
      .then((response) => response.data);
  }

  getMissionById(id: string): Promise<Mission> {
    return apiService.get(`/missions/${id}`).then((response) => response.data);
  }

  createMission(missionData: any): Promise<Mission> {
    // Create a copy and ensure data is formatted correctly
    const formattedData = { ...missionData };

    // If we have a gameId and action is play related, ensure the gameId is included
    if (
      formattedData.gameId &&
      (formattedData.action === MissionAction.PLAY_GAME ||
        formattedData.action === MissionAction.PLAY_NEW_GAME)
    ) {
      // Keep gameId as is
    } else if (
      formattedData.action !== MissionAction.PLAY_GAME &&
      formattedData.action !== MissionAction.PLAY_NEW_GAME
    ) {
      // Remove gameId if the action doesn't require it
      delete formattedData.gameId;
    }

    return apiService
      .post('/missions', formattedData)
      .then((response) => response.data);
  }

  updateMission(id: string, missionData: any): Promise<Mission> {
    return apiService
      .put(`/missions/${id}`, missionData)
      .then((response) => response.data);
  }

  deleteMission(id: string): Promise<Mission> {
    return apiService
      .delete(`/missions/${id}`)
      .then((response) => response.data);
  }

  // Public methods
  getActiveMissions(): Promise<Mission[]> {
    return apiService.get('/missions').then((response) => response.data);
  }

  getMissionsByType(type: MissionType, limit = 10): Promise<Mission[]> {
    return apiService
      .get(`/missions/type/${type}`, { params: { limit } })
      .then((response) => response.data);
  }

  // Complete a mission (user action)
  completeMission(missionId: string, userId: string): Promise<any> {
    return apiService
      .post(`/missions/${missionId}/complete`, { userId })
      .then((response) => response.data);
  }
}

const missionService = new MissionService();
export default missionService;
